/**
 * Immediate-window *model* — the pure, DOM-free heart of the REPL.
 *
 * Responsibilities (mirroring the lesson engine's model/view split):
 *   - Hold the **transcript** of past runs ({@link ReplEntry} values).
 *   - Hold the **command history** and a navigation cursor so ↑/↓ recall works
 *     like a real terminal (including stashing the in-progress draft).
 *   - **Persist** both to storage (browser `localStorage` by default) so the
 *     kid's history and last session survive a reload — and **restore** them on
 *     construction.
 *
 * Everything here is synchronous and side-effect-light: the actual Python run
 * lives in `runner.ts`, and the DOM lives in `repl-view.ts`. Keeping this layer
 * pure lets us assert history/persistence behaviour exhaustively in node tests.
 */
import type { OutputChunk } from "../pyodide/types";
import type { FriendlyError } from "./errors";

/** One run in the transcript: the input plus everything it produced. */
export interface ReplEntry {
  /** The exact source the kid submitted (may be multiple lines). */
  input: string;
  /** Captured stdout/stderr chunks, in arrival order. */
  outputs: OutputChunk[];
  /** repr of the last expression's value, or null when there was none. */
  result: string | null;
  /** A friendly error when the run failed, or null on success. */
  error: FriendlyError | null;
  /** Convenience flag: did the run finish without an error? */
  ok: boolean;
}

/** The tiny slice of `localStorage` the model needs (also easy to fake). */
export interface ReplStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

export interface ReplSessionOptions {
  /** Persistence backend; defaults to `localStorage`, else in-memory. */
  storage?: ReplStorage;
  /** Cap on retained history commands and transcript entries (default 200). */
  limit?: number;
}

/** Storage keys — namespaced so they never collide with the lesson progress. */
export const HISTORY_KEY = "sprocket.repl.history";
export const SESSION_KEY = "sprocket.repl.session";

/** The model surface the view (and tests) drive. */
export interface ReplSession {
  /** Submitted commands, oldest → newest. */
  readonly history: readonly string[];
  /** Transcript of past runs, oldest → newest. */
  readonly entries: readonly ReplEntry[];

  /** Record a completed run: append the entry + remember its input. Persists. */
  record(entry: ReplEntry): void;

  /**
   * Recall the previous command (↑). `draft` is the kid's current, unsubmitted
   * text — stashed on the first step back so ↓ can return to it. Returns the
   * recalled command, or null when there's no older command to show.
   */
  historyPrev(draft: string): string | null;
  /**
   * Recall the next command (↓). Returns the next command, the stashed draft
   * when stepping back to "now", or null when already at the live prompt.
   */
  historyNext(): string | null;
  /** Drop back to the live prompt (called after a submit or manual edit). */
  resetHistoryCursor(): void;

  /** Wipe history + transcript (and storage). */
  clear(): void;

  /** Subscribe to model changes; returns an unsubscribe fn. */
  onChange(listener: () => void): () => void;
}

/** Process-local fallback when no real `localStorage` is available. */
function memoryStorage(): ReplStorage {
  const data = new Map<string, string>();
  return {
    getItem: (k) => (data.has(k) ? (data.get(k) as string) : null),
    setItem: (k, v) => {
      data.set(k, v);
    },
  };
}

/** Resolve a default storage backend safely (sandboxed access can throw). */
function defaultStorage(): ReplStorage {
  try {
    const ls = (globalThis as { localStorage?: ReplStorage }).localStorage;
    if (ls) return ls;
  } catch {
    /* access blocked (private mode / SSR) — fall through to memory. */
  }
  return memoryStorage();
}

/** Parse a JSON array from storage, tolerating absent/corrupt values. */
function readArray<T>(storage: ReplStorage, key: string): T[] {
  try {
    const raw = storage.getItem(key);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? (parsed as T[]) : [];
  } catch {
    return [];
  }
}

/** Create a REPL session, restoring any persisted history + transcript. */
export function createReplSession(
  options: ReplSessionOptions = {},
): ReplSession {
  const storage = options.storage ?? defaultStorage();
  const limit = options.limit ?? 200;

  const history: string[] = readArray<string>(storage, HISTORY_KEY);
  const entries: ReplEntry[] = readArray<ReplEntry>(storage, SESSION_KEY);

  // cursor === history.length → at the live prompt (showing the draft).
  let cursor = history.length;
  let stashedDraft = "";
  const listeners = new Set<() => void>();

  const emit = (): void => {
    for (const listener of listeners) listener();
  };

  const persist = (): void => {
    try {
      storage.setItem(HISTORY_KEY, JSON.stringify(history));
      storage.setItem(SESSION_KEY, JSON.stringify(entries));
    } catch {
      /* storage full / blocked — the in-memory state is still correct. */
    }
  };

  /** Keep arrays from growing without bound (oldest entries fall off). */
  const trim = (arr: unknown[]): void => {
    if (arr.length > limit) arr.splice(0, arr.length - limit);
  };

  return {
    get history() {
      return history;
    },
    get entries() {
      return entries;
    },

    record(entry) {
      entries.push(entry);
      trim(entries);

      const command = entry.input;
      // Skip blank commands and consecutive duplicates (terminal-like).
      if (command.trim() && history[history.length - 1] !== command) {
        history.push(command);
        trim(history);
      }
      // A new command resets the recall cursor to the live prompt.
      cursor = history.length;
      stashedDraft = "";

      persist();
      emit();
    },

    historyPrev(draft) {
      if (history.length === 0) return null;
      if (cursor === history.length) stashedDraft = draft;
      if (cursor > 0) cursor -= 1;
      return history[cursor];
    },

    historyNext() {
      if (cursor >= history.length) return null;
      cursor += 1;
      if (cursor === history.length) return stashedDraft;
      return history[cursor];
    },

    resetHistoryCursor() {
      cursor = history.length;
      stashedDraft = "";
    },

    clear() {
      history.length = 0;
      entries.length = 0;
      cursor = 0;
      stashedDraft = "";
      persist();
      emit();
    },

    onChange(listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
  };
}

/* -------------------------------------------------------------------------- */
/*  Multi-line continuation heuristic                                          */
/* -------------------------------------------------------------------------- */

/**
 * Decide whether a submitted source *looks unfinished* and so should get a new
 * line rather than run. This gives kids natural multi-line editing without a
 * full editor: pressing Enter after `for i in range(3):` keeps typing the body,
 * while a complete one-liner runs immediately.
 *
 * Rules (deliberately simple and forgiving):
 *   - A trailing `:` (a block header) expects an indented body next.
 *   - A trailing `\` is an explicit line continuation.
 *   - Unbalanced (, [ or { means we're still mid-expression.
 *   - But a final **blank** line always ends the block — the kid's "I'm done"
 *     signal, exactly like the standard Python shell.
 */
export function needsContinuation(source: string): boolean {
  if (source.length === 0) return false;

  const lines = source.split("\n");
  const lastLine = lines[lines.length - 1];
  // A blank final line is the universal "run it now" signal.
  if (lastLine.trim() === "" && lines.length > 1) return false;

  const trimmed = source.trimEnd();
  if (trimmed.endsWith(":")) return true;
  if (trimmed.endsWith("\\")) return true;
  if (hasUnbalancedBrackets(source)) return true;

  return false;
}

/**
 * Lightweight bracket-balance check that skips over the contents of quoted
 * strings (so a `"("` in a message doesn't confuse the count). Handles single
 * and double quotes and basic backslash escapes — plenty for kid-sized code.
 */
function hasUnbalancedBrackets(source: string): boolean {
  let depth = 0;
  let quote: string | null = null;
  let escaped = false;

  for (const ch of source) {
    if (quote) {
      if (escaped) {
        escaped = false;
      } else if (ch === "\\") {
        escaped = true;
      } else if (ch === quote) {
        quote = null;
      }
      continue;
    }
    if (ch === '"' || ch === "'") {
      quote = ch;
    } else if (ch === "(" || ch === "[" || ch === "{") {
      depth += 1;
    } else if (ch === ")" || ch === "]" || ch === "}") {
      depth -= 1;
    }
  }
  return depth > 0;
}

/* -------------------------------------------------------------------------- */
/*  Output helpers                                                             */
/* -------------------------------------------------------------------------- */

/**
 * Flatten an entry's captured output into the single string the lesson engine's
 * success checks evaluate against (stdout/stderr text, then the result repr).
 * The friendly-error text is intentionally excluded — a failed run shouldn't
 * accidentally satisfy an `output-contains` check.
 */
export function entryOutputText(entry: ReplEntry): string {
  const streamed = entry.outputs.map((o) => o.text).join("");
  if (entry.result !== null && entry.result !== undefined) {
    const sep = streamed.length > 0 && !streamed.endsWith("\n") ? "\n" : "";
    return streamed + sep + entry.result;
  }
  return streamed;
}
